import { useState, useMemo, useCallback } from 'react'
import { Box, Checkbox, Typography, Button, IconButton, TextField, InputAdornment } from '@mui/material'
import { FullscreenExit } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { icons } from '../../../../../utils/icons'
import CustomAccordion from '../../../../../components/accordion'
import { FileModal } from '../../../../reportFolio/pages/dashboard/components/fileModal'
import { EvidencesTypeValueEnum } from '../../../../../utils/enums/evidences'
import { Step3EvidencesProps } from '../types'
import { useStyles } from '../styles'
import { CatalogAnswerDetail, CatalogAnswerBadge } from '../../form/components/catalogAnswerDetail'

function Step3Evidences({ planInfo, data, onChange }: Step3EvidencesProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [allExpanded, setAllExpanded] = useState(false)
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({})
  const [modal, setModal] = useState<{ open: boolean; src: string; type: EvidencesTypeValueEnum }>({
    open: false,
    src: '',
    type: EvidencesTypeValueEnum.IMAGE,
  })

  const mapEvidenceType = useCallback((type: string): EvidencesTypeValueEnum => {
    switch (type) {
      case 'image':
        return EvidencesTypeValueEnum.IMAGE
      case 'video':
        return EvidencesTypeValueEnum.VIDEO
      case 'sign':
        return EvidencesTypeValueEnum.SIGN
      case 'documents':
        return EvidencesTypeValueEnum.DOCUMENT
      case 'comment':
        return EvidencesTypeValueEnum.COMMENT
      default:
        return EvidencesTypeValueEnum.IMAGE
    }
  }, [])

  const displayMedia = useCallback((src: string, type: string) => {
    const mappedType = mapEvidenceType(type)
    const modalType = mappedType === EvidencesTypeValueEnum.SIGN
      ? EvidencesTypeValueEnum.IMAGE
      : mappedType
    setModal({ open: true, src, type: modalType })
  }, [mapEvidenceType])

  const closeModal = useCallback(() => {
    setModal((prev) => ({ ...prev, open: false }))
  }, [])

  const selectedQuestions = useMemo(() => {
    return (planInfo.questions || []).filter((q) => data.selectedQuestions.includes(q.id))
  }, [planInfo.questions, data.selectedQuestions])

  const filteredQuestions = useMemo(() => {
    if (!searchTerm.trim()) return selectedQuestions
    return selectedQuestions.filter((q) =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [selectedQuestions, searchTerm])

  const currentQuestion = useMemo(() => {
    const found = selectedQuestions.find((q) => q.id === data.selectedQuestionId)
    return found || selectedQuestions[0] || null
  }, [selectedQuestions, data.selectedQuestionId])

  const evidences = useMemo(() => {
    return currentQuestion?.evidences || []
  }, [currentQuestion])

  const evidencesByType = useMemo(() => {
    const groups: Record<string, typeof evidences> = {
      image: [],
      video: [],
      comment: [],
      sign: [],
      documents: [],
    }
    evidences.forEach((ev) => {
      if (groups[ev.type]) groups[ev.type].push(ev)
    })
    return groups
  }, [evidences])

  const toggleAll = useCallback(() => {
    const newState = !allExpanded
    setAllExpanded(newState)
    const newExpanded: Record<string, boolean> = {}
    Object.entries(evidencesByType).forEach(([type, items]) => {
      if (items.length > 0) newExpanded[type] = newState
    })
    setExpandedAccordions(newExpanded)
  }, [allExpanded, evidencesByType])

  const isEvidenceSelected = useCallback(
    (evidenceId: number) =>
      !!currentQuestion &&
      data.selectedEvidences.some(
        (item) => item.questionId === currentQuestion.id && item.evidenceId === evidenceId
      ),
    [currentQuestion, data.selectedEvidences]
  )

  const toggleEvidence = useCallback(
    (evidenceId: number) => {
      if (!currentQuestion) return

      const alreadySelected = isEvidenceSelected(evidenceId)
      const newSelected = alreadySelected
        ? data.selectedEvidences.filter(
            (item) => !(item.questionId === currentQuestion.id && item.evidenceId === evidenceId)
          )
        : [...data.selectedEvidences, { questionId: currentQuestion.id, evidenceId }]

      onChange({ selectedEvidences: newSelected })
    },
    [currentQuestion, data.selectedEvidences, isEvidenceSelected, onChange]
  )

  const toggleAllByType = useCallback(
    (type: string) => {
      if (!currentQuestion) return

      const typeEvidences = evidencesByType[type] || []
      const typeIds = typeEvidences.map((ev) => ev.id)

      const currentSelectedIds = data.selectedEvidences
        .filter((item) => item.questionId === currentQuestion.id)
        .map((item) => item.evidenceId)

      const allSelected = typeIds.every((id) => currentSelectedIds.includes(id))

      const withoutCurrentType = data.selectedEvidences.filter(
        (item) => !(item.questionId === currentQuestion.id && typeIds.includes(item.evidenceId))
      )

      const newSelected = allSelected
        ? withoutCurrentType
        : [
            ...withoutCurrentType,
            ...typeIds.map((id) => ({ questionId: currentQuestion.id, evidenceId: id })),
          ]

      onChange({ selectedEvidences: newSelected })
    },
    [currentQuestion, evidencesByType, data.selectedEvidences, onChange]
  )

  const handleQuestionSelect = useCallback(
    (questionId: number) => {
      onChange({ selectedQuestionId: questionId })
    },
    [onChange]
  )

  return (
    <Box sx={styles.step3Container}>
      {/* Left Column - Questions */}
      <Box sx={styles.step3LeftColumn}>
        <Box sx={styles.questionsHeader}>
          <Typography sx={styles.columnHeader}>
            {t('actionPlan.questions')}
          </Typography>
        </Box>
        <Box sx={styles.questionsContent}>
          <Box sx={styles.searchContainerStep3}>
            <TextField
              fullWidth
              placeholder={t('general.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {icons('search')}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={styles.questionList}>
            {filteredQuestions.map((question) => (
              <Box
                key={question.id}
                sx={[
                  styles.questionItemSelectable,
                  question.id === currentQuestion?.id && styles.questionItemSelected,
                ]}
                onClick={() => handleQuestionSelect(question.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography sx={styles.questionItemText}>
                    {question.question}
                  </Typography>
                  {!!question.fullAnswer?.length && (
                    <CatalogAnswerBadge count={question.fullAnswer.length} />
                  )}
                </Box>
                {question.fullAnswer?.length ? (
                  <CatalogAnswerDetail fullAnswer={question.fullAnswer} showPathLabel={false} />
                ) : (
                  <>
                    <Typography sx={styles.questionItemAnswerLabel}>
                      {t('actionPlan.selectedAnswer')}
                    </Typography>
                    <Typography sx={styles.questionItemAnswerValue}>
                      {question.answer}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Column - Evidences */}
      <Box sx={styles.step3RightColumn}>
        <Box sx={styles.evidencesHeader}>
          <Typography sx={styles.columnHeader}>
            {t('actionPlan.evidences')}
          </Typography>
          {evidences.length > 0 && (
            <Button onClick={toggleAll} sx={styles.expandAllButton}>
              {allExpanded ? t('actionPlan.collapseAll') : t('actionPlan.expandAll')}
            </Button>
          )}
        </Box>

        <Box sx={styles.evidencesList}>
          {evidences.length === 0 ? (
            <Typography sx={styles.noEvidencesMessage}>
              {t('actionPlan.noEvidencesForQuestion')}
            </Typography>
          ) : (
            Object.entries(evidencesByType).map(([type, typeEvidences]) => {
              if (typeEvidences.length === 0) return null
              const count = typeEvidences.length
              const allTypeSelected = typeEvidences.every((ev) =>
                isEvidenceSelected(ev.id)
              )

              return (
                <Box sx={styles.accordionWrapper} key={type}>
                  <CustomAccordion
                    title={
                      <Box sx={styles.accordionTitle}>
                        <Box sx={styles.accordionBadge}>{count}</Box>
                        <Typography sx={styles.accordionTitleText}>
                          {t(`general.evidences.${type}`)}
                        </Typography>
                      </Box>
                    }
                    expandIcon={<Box sx={{ '& svg': { display: 'flex', alignItems: 'center', fontSize: '32px' } }}>{icons('arrow-down')}</Box>}
                    description={
                      <Box>
                        <Box sx={styles.selectAllRow}>
                          <Typography
                            sx={styles.selectAllText}
                            onClick={() => toggleAllByType(type)}
                          >
                            {t('actionPlan.selectAll')}
                          </Typography>
                        </Box>
                        <Box sx={['image', 'video', 'sign'].includes(type) ? styles.evidenceGrid : styles.evidenceList}>
                          {typeEvidences.map((evidence) => (
                            ['image', 'video', 'sign'].includes(type) ? (
                              <Box key={evidence.id} sx={styles.evidenceItem}>
                                <Checkbox
                                  checked={isEvidenceSelected(evidence.id)}
                                  onChange={() => toggleEvidence(evidence.id)}
                                  sx={styles.evidenceCheckboxAbsolute}
                                />
                                <Box sx={styles.evidencePreview}>
                                  {type === 'video' ? (
                                    <video style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
                                      <source src={evidence.src} type="video/mp4" />
                                    </video>
                                  ) : (
                                    <img
                                      src={evidence.src}
                                      alt={evidence.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  )}
                                  <IconButton
                                    onClick={() => displayMedia(evidence.src, evidence.type)}
                                    sx={styles.evidenceExpand}
                                  >
                                    <FullscreenExit sx={{ width: '100%', height: '100%' }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box key={evidence.id} sx={styles.evidenceListItem}>
                                <Box sx={styles.evidenceListItemContent}>
                                  <Checkbox
                                    checked={isEvidenceSelected(evidence.id)}
                                    onChange={() => toggleEvidence(evidence.id)}
                                    sx={styles.evidenceCheckboxAbsolute}
                                  />
                                  <Typography sx={styles.evidenceListItemName}>
                                    {evidence.name}
                                  </Typography>
                                </Box>
                                {evidence.type === 'documents' && (
                                  <Box sx={styles.evidenceListItemView}>
                                    {icons('visibility')}
                                  </Box>
                                )}
                              </Box>
                            )
                          ))}
                        </Box>
                      </Box>
                    }
                    expanded={!!expandedAccordions[type]}
                    onChange={(expand) =>
                      setExpandedAccordions((prev) => ({ ...prev, [type]: expand }))
                    }
                  />
                </Box>
              )
            })
          )}
        </Box>
      </Box>

      <FileModal
        src={modal.src}
        type={modal.type}
        modalProps={{
          open: modal.open,
          addClose: true,
          close: { onClick: closeModal },
        }}
      />
    </Box>
  )
}

export default Step3Evidences
